// src/components/backoffice/explanations/ExplanationsPage.tsx

import React, { useState, useEffect, useCallback } from "react";
import {
  getExplanations,
  getExplanationDetails,
  saveExplanation,
  deleteExplanation,
  restoreExplanation,
  ExplanationDetails,
  GetExplanationsParams,
  GetExplanationsResponse,
  SaveExplanationData,
} from "../../../services/backoffice/explanationsService";

const ExplanationsPage: React.FC = () => {
  // State for explanations data
  const [explanationsData, setExplanationsData] =
    useState<GetExplanationsResponse>({
      explanations: [],
      total: 0,
      page: 1,
      limit: 25,
      totalPages: 0,
    });

  // State for loading and errors
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{
    [key: number]: boolean;
  }>({});

  // State for filters
  const [filters, setFilters] = useState<GetExplanationsParams>({
    page: 1,
    limit: 25,
    search: "",
    is_active: true, // ✅ שונה מ-show_deleted ל-is_active
  });

  // State for add/edit explanation
  const [editingExplanation, setEditingExplanation] =
    useState<ExplanationDetails | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<SaveExplanationData>({
    id: undefined,
    step_key: "", // ✅ נוסף
    step_name: "", // ✅ נוסף
    content: "", // זה יהפוך ל-explanation_text בשרת
    order_index: undefined,
  });
  const [formLoading, setFormLoading] = useState<boolean>(false);

  // Load explanations function
  const loadExplanations = useCallback(async () => {
    try {
      console.log("📄 טוען הסברים עם פילטרים:", filters);
      setLoading(true);
      setError(null);
      const data = await getExplanations(filters);
      console.log("📄 התקבלו הסברים:", data);
      setExplanationsData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "שגיאה בטעינת ההסברים");
      console.error("Error loading explanations:", err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load explanations on component mount and filter changes
  useEffect(() => {
    loadExplanations();
  }, [loadExplanations]);

  // Handle filter changes
  const handleFilterChange = (key: keyof GetExplanationsParams, value: any) => {
    console.log(`פילטר ${key} השתנה ל:`, value);

    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [key]: value,
        page: key !== "page" ? 1 : value,
      };

      console.log("פילטרים חדשים:", newFilters);
      return newFilters;
    });
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // The search will trigger automatically via useEffect
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    handleFilterChange("page", newPage);
  };

  // Handle add new explanation
  const handleAddExplanation = () => {
    setEditingExplanation(null);
    setFormData({
      id: undefined,
      step_key: "",
      step_name: "",
      content: "",
      order_index: undefined,
    });
    setShowModal(true);
  };

  // Handle edit explanation
  const handleEditExplanation = async (explanationId: number) => {
    try {
      setLoading(true);
      console.log("✏️ מתחיל עריכת הסבר:", explanationId);

      const explanationDetails = await getExplanationDetails(explanationId);
      console.log("✏️ נתוני הסבר שהתקבלו:", explanationDetails);

      setEditingExplanation(explanationDetails);

      // ✅ מיפוי נכון של השדות מהשרת
      const newFormData = {
        id: explanationDetails.id,
        step_key: explanationDetails.step_key || "",
        step_name: explanationDetails.step_name || "",
        content: explanationDetails.explanation_text || "", // explanation_text → content
        order_index: explanationDetails.order_index,
      };

      console.log("✏️ FormData חדש שיוגדר:", newFormData);
      setFormData(newFormData);

      setTimeout(() => {
        setShowModal(true);
        console.log("✏️ showModal הוגדר ל-true");
      }, 100);
    } catch (err) {
      console.error("שגיאה בטעינת פרטי ההסבר:", err);
      setError(err instanceof Error ? err.message : "שגיאה בטעינת פרטי ההסבר");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete/restore explanation
  const handleToggleDelete = async (
    explanationId: number,
    isDeleted: boolean
  ) => {
    const action = isDeleted ? "לשחזר" : "למחוק";
    if (!window.confirm(`האם אתה בטוח שברצונך ${action} את ההסבר?`)) {
      return;
    }

    try {
      setActionLoading((prev) => ({ ...prev, [explanationId]: true }));

      if (isDeleted) {
        await restoreExplanation(explanationId);
      } else {
        await deleteExplanation(explanationId);
      }

      await loadExplanations();
    } catch (err) {
      console.error("שגיאה בעדכון הסבר:", err);
      setError(err instanceof Error ? err.message : "שגיאה בעדכון ההסבר");
    } finally {
      setActionLoading((prev) => ({ ...prev, [explanationId]: false }));
    }
  };

  // Handle form submit
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ✅ ולידציה מעודכנת לשדות הנכונים
    if (!formData.step_key?.trim()) {
      setError("מפתח שלב (Step Key) נדרש");
      return;
    }

    if (!formData.step_name?.trim()) {
      setError("שם השלב (Step Name) נדרש");
      return;
    }

    if (!formData.content?.trim()) {
      setError("תוכן ההסבר נדרש");
      return;
    }

    try {
      setFormLoading(true);
      setError(null);

      console.log("💾 שומר הסבר:", formData);
      await saveExplanation(formData);

      setShowModal(false);
      setEditingExplanation(null);
      await loadExplanations();

      console.log("✅ הסבר נשמר בהצלחה");
    } catch (err) {
      setError(err instanceof Error ? err.message : "שגיאה בשמירת ההסבר");
      console.error("Error saving explanation:", err);
    } finally {
      setFormLoading(false);
    }
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setShowModal(false);
    setEditingExplanation(null);
    setFormData({
      id: undefined,
      step_key: "",
      step_name: "",
      content: "",
      order_index: undefined,
    });
    setError(null);
  };

  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return "לא זמין";

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        console.warn("תאריך לא תקין:", dateString);
        return "תאריך לא תקין";
      }

      return date.toLocaleDateString("he-IL", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("שגיאה בפורמט תאריך:", error, dateString);
      return "שגיאה בתאריך";
    }
  };

  // Truncate content for table display
  const truncateContent = (content: string, maxLength: number = 100) => {
    if (!content) return "";
    const textContent = content.replace(/<[^>]*>/g, ""); // Remove HTML tags
    return textContent.length > maxLength
      ? textContent.substring(0, maxLength) + "..."
      : textContent;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          ניהול הסברי חישוב 📝
        </h1>
        <button
          onClick={handleAddExplanation}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
        >
          הוספת הסבר חדש
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button
            onClick={() => setError(null)}
            className="float-left ml-2 text-red-700 hover:text-red-900"
          >
            ✕
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <form
          onSubmit={handleSearch}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              חיפוש
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="חפש לפי מפתח, שם או תוכן..."
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              סטטוס הסברים
            </label>
            <select
              value={filters.is_active === null ? "all" : filters.is_active ? "1" : "0"}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "all") {
                  handleFilterChange("is_active", null);
                } else {
                  handleFilterChange("is_active", value === "1");
                }
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="1">פעילים בלבד</option>
              <option value="0">מחוקים בלבד</option>
              <option value="all">הכל</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              פריטים בעמוד
            </label>
            <select
              value={filters.limit}
              onChange={(e) =>
                handleFilterChange("limit", parseInt(e.target.value))
              }
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              type="submit"
              className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
            >
              חפש
            </button>
          </div>
        </form>
      </div>

      {/* Explanations Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="text-gray-500">טוען הסברים...</div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-right text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th className="px-6 py-3">ID</th>
                    <th className="px-6 py-3">מפתח שלב</th>
                    <th className="px-6 py-3">שם השלב</th>
                    <th className="px-6 py-3">תוכן (קטע)</th>
                    <th className="px-6 py-3">סדר</th>
                    <th className="px-6 py-3">סטטוס</th>
                    <th className="px-6 py-3">תאריך יצירה</th>
                    <th className="px-6 py-3">פעולות</th>
                  </tr>
                </thead>
                <tbody>
                  {explanationsData.explanations.map((explanation) => (
                    <tr
                      key={explanation.id}
                      className="bg-white border-b hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {explanation.id}
                      </td>
                      <td className="px-6 py-4">
                        <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-mono">
                          {explanation.step_key}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {explanation.step_name}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {truncateContent(explanation.explanation_text || "")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                          {explanation.order_index}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 text-xs rounded-full ${
                            !explanation.is_active
                              ? "bg-red-100 text-red-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {!explanation.is_active ? "מחוק" : "פעיל"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {formatDate(explanation.ts)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              handleEditExplanation(explanation.id)
                            }
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1 rounded transition-colors bg-blue-50 hover:bg-blue-100"
                          >
                            ✏️ ערוך
                          </button>

                          <button
                            onClick={() =>
                              handleToggleDelete(
                                explanation.id,
                                !explanation.is_active
                              )
                            }
                            disabled={actionLoading[explanation.id]}
                            className={`text-sm font-medium px-2 py-1 rounded transition-colors ${
                              !explanation.is_active
                                ? "text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100"
                                : "text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {actionLoading[explanation.id]
                              ? "מעדכן..."
                              : !explanation.is_active
                              ? "♻️ שחזר"
                              : "🗑️ מחק"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {explanationsData.totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    מציג{" "}
                    {(explanationsData.page - 1) * explanationsData.limit + 1}{" "}
                    עד{" "}
                    {Math.min(
                      explanationsData.page * explanationsData.limit,
                      explanationsData.total
                    )}{" "}
                    מתוך {explanationsData.total} הסברים
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() =>
                        handlePageChange(explanationsData.page - 1)
                      }
                      disabled={explanationsData.page === 1}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      קודם
                    </button>

                    {Array.from(
                      { length: explanationsData.totalPages },
                      (_, i) => i + 1
                    ).map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 text-sm border rounded-md ${
                          pageNum === explanationsData.page
                            ? "bg-blue-500 text-white border-blue-500"
                            : "bg-white border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}

                    <button
                      onClick={() =>
                        handlePageChange(explanationsData.page + 1)
                      }
                      disabled={
                        explanationsData.page === explanationsData.totalPages
                      }
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      הבא
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-[90%] max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingExplanation
                  ? `עריכת הסבר: ${editingExplanation.step_name}`
                  : "הוספת הסבר חדש"}
              </h3>

              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      מפתח שלב (Step Key) *
                    </label>
                    <input
                      type="text"
                      value={formData.step_key || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          step_key: e.target.value,
                        }))
                      }
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                      placeholder="step_1, step_2, etc."
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      מזהה ייחודי טכני (step_1, step_2, וכו')
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      שם השלב (Step Name) *
                    </label>
                    <input
                      type="text"
                      value={formData.step_name || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          step_name: e.target.value,
                        }))
                      }
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="חישוב מכס, חישוב מס רכישה, etc."
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      שם השלב כפי שיוצג למשתמש
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    תוכן ההסבר *
                  </label>
                  <textarea
                    value={formData.content || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        content: e.target.value,
                      }))
                    }
                    rows={10}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="הזן את תוכן ההסבר (ניתן להשתמש ב-HTML)"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ניתן להשתמש בתגי HTML בסיסיים כמו &lt;p&gt;, &lt;strong&gt;,
                    &lt;em&gt;, &lt;ul&gt;, &lt;li&gt; וכו'
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    סדר תצוגה (אופציונלי)
                  </label>
                  <input
                    type="number"
                    value={formData.order_index || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        order_index: e.target.value ? parseInt(e.target.value) : undefined,
                      }))
                    }
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0, 1, 2..."
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    אם לא מוגדר, יוגדר אוטומטית כמספר הגבוה ביותר + 1
                  </p>
                </div>

                <div className="flex space-x-4 pt-4">
                  <button
                    type="submit"
                    disabled={formLoading}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md disabled:opacity-50 transition-colors"
                  >
                    {formLoading ? "שומר..." : "שמור"}
                  </button>
                  <button
                    type="button"
                    onClick={handleFormCancel}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-md transition-colors"
                  >
                    ביטול
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplanationsPage;